import yaml from 'js-yaml';
import { v4 as uuidv4 } from 'uuid';
import { Edge, Node, MarkerType } from 'reactflow';
import isEqual from 'lodash/isEqual';
import {
  DEFAULT_CORRELATION_RULE_YAML_EVALUATION_ORDER,
  DEFAULT_CORRELATION_RULE_YAML_GROUP,
  DEFAULT_CORRELATION_RULE_YAML_LOOKBACK_MINUTES,
  DEFAULT_CORRELATION_RULE_YAML_MATCH_CRITERIA,
  DEFAULT_CORRELATION_RULE_YAML_SCHEDULE,
  DEFAULT_CORRELATION_RULE_YAML_SEQUENCE,
  DEFAULT_CORRELATION_RULE_YAML_TRANSITIONS,
} from 'Source/constants';
import {
  ALERT_NODE_WIDTH_PX,
  CR_TRANSITION_ARROW_HEIGHT,
  CR_TRANSITION_ARROW_WIDTH,
  GROUP_NODE_WIDTH_PX,
  NODE_SPACING,
  STEP_NODE_MAX_HEIGHT_PX,
  STEP_NODE_WIDTH_PX,
} from './constantsV3';
import { StepNodeData } from './StepNode/StepNodeV3';
import { TransitionEdgeData } from './TransitionEdgeV3';
import { GroupNodeData } from './GroupNodeV3';

export enum PAGE_TAB {
  LOGIC = 'rule-logic',
  ALERTS = 'alerts',
  ERRORS = 'errors',
}

export enum FlowChartElementTypes {
  StepNode = 'step-node',
  TransitionEdge = 'transition-edge',
  GroupNode = 'group-node',
  AlertNode = 'alert-node',
}

export enum YAMLField {
  Sequence = 'Sequence',
  Transitions = 'Transitions',
  Group = 'Group',
  MatchCriteria = 'MatchCriteria',
  MinMatchCount = 'MinMatchCount',
  Schedule = 'Schedule',
  EventEvaluationOrder = 'EventEvaluationOrder',
  LookbackWindowMinutes = 'LookbackWindowMinutes',
}

export interface CorrelationRuleData {
  [YAMLField.Sequence]?: RuleStepData[];
  [YAMLField.Transitions]?: TransitionData[];
  [YAMLField.Group]?: RuleStepData[];
  [YAMLField.MatchCriteria]?: Record<string, MatchCriteriaData>;
  [YAMLField.MinMatchCount]?: number;
  [YAMLField.Schedule]?: ScheduleData;
  [YAMLField.EventEvaluationOrder]?: string;
  [YAMLField.LookbackWindowMinutes]?: number;
}

export const SequenceFields = [YAMLField.Sequence, YAMLField.Transitions];
export const GroupFields = [YAMLField.Group, YAMLField.MatchCriteria];

export interface RuleStepData {
  ID?: string;
  RuleID: string;
  SignalMatch?: string;
  MinMatchCount?: number;
  MaxMatchCount?: number;
  Absence?: boolean;
}

export interface MatchData {
  On?: string;
  From?: string;
  To?: string;
}

export interface TransitionData {
  ID: string;
  From: string;
  To: string;
  WithinTimeFrameMinutes?: number;
  Match: MatchData;
}

export interface MatchCriteriaData {
  GroupID: string;
  Match: string;
}

export interface ScheduleData {
  ChronExpression?: string;
  RateMinutes?: number;
  TimeoutMinutes: number;
}

export const CORRELATION_RULE_EXCEPTIONS = {
  NO_ID_SEQUENCE: 'Missing ID in Sequence',
  NO_RULE_ID_SEQUENCE: 'Missing RuleID in Sequence',
  NO_ID_GROUP: 'Missing RuleID in Group',
  DUPLICATE_ID: 'Cannot have duplicate IDs',
  DUPLICATE_RULE_ID: 'Cannot have duplicate RuleIDs',
  INVALID_TRANSITIONS:
    'Invalid Transitions: Can only have one transition between each node in order',
  NO_SELF_REFERENCE: 'A rule cannot reference itself',
  MISSING_TRANSITION: 'Missing transition',
  TOO_MANY_TRANSITIONS: 'Too many transitions',
  INVALID_YAML: 'Invalid YAML',
};

const getMarkerEnd = (color?: string) => ({
  width: CR_TRANSITION_ARROW_WIDTH,
  height: CR_TRANSITION_ARROW_HEIGHT,
  type: MarkerType.Arrow,
  ...(color ? { color } : {}),
});

type RulesToNodesAndEdgesResult = {
  nodes: Array<Node<StepNodeData | GroupNodeData>>;
  edges: Array<Edge<TransitionEdgeData>>;
  error?: string;
  warning?: string;
};
// TODO: slugify() ids because they are DOM IDs
/**
 * Translates YAML rule data into nodes and edges readable by React Flow to build a flow chart
 * @param {string} ruleYAML The YAML correlation rule string to load
 * @param {string} [ruleID] The ID of the correlation rule being parsed to check for self-reference
 * @param {boolean} [showAlertNode] Controls if the Alert node should be added as the last node
 * @returns {RulesToNodesAndEdgesResult} The YAML parsed into nodes & edges for reactflow, with
 *  potential warning and/or error.
 */
export const ruleToNodesAndEdges = (
  ruleYAML: string,
  ruleID?: string,
  showAlertNode?: boolean,
  markerColor?: string,
): RulesToNodesAndEdgesResult => {
  const markerEnd = getMarkerEnd(markerColor);
  let nodes = [];
  const edges = [];
  const transitionMap = { outOfOrderTransitions: [] };
  const seenIDs = [];
  const seenRuleIDs = [];
  let parsedYaml: Array<CorrelationRuleData>;

  try {
    parsedYaml = yaml.load(ruleYAML) as Array<CorrelationRuleData>;
  } catch (error) {
    return { nodes, edges, error: error.message };
  }
  if (typeof parsedYaml !== 'object') {
    return { nodes, edges, error: CORRELATION_RULE_EXCEPTIONS.INVALID_YAML };
  }
  const parsedRule = (parsedYaml || [])[0];
  // filters out empty list items when a user is starting to type a new item
  const sequence = parsedRule?.Sequence?.filter(Boolean);
  const transitions = parsedRule?.Transitions;
  const hasTransitions = Boolean(transitions?.length);
  const group = parsedRule?.Group?.filter(Boolean);
  const hasGroup = Boolean(group?.length);

  const stepList = (sequence || group || []).map((origItem, index) => {
    const item = { ...origItem };
    let error: string;

    if (item?.ID) {
      if (seenIDs.includes(item.ID)) {
        error = CORRELATION_RULE_EXCEPTIONS.DUPLICATE_ID;
      } else {
        seenIDs.push(item.ID);
      }
    } else if (hasTransitions) {
      // if there are transitions, then ID is required
      error = CORRELATION_RULE_EXCEPTIONS.NO_ID_SEQUENCE;
      // Add a unique ID so we can still display an error on the node
      item.ID = uuidv4();
    }
    if (item.RuleID) {
      if (ruleID && item.RuleID === ruleID) {
        error = CORRELATION_RULE_EXCEPTIONS.NO_SELF_REFERENCE;
      } else if (seenRuleIDs.includes(item.RuleID)) {
        error = CORRELATION_RULE_EXCEPTIONS.DUPLICATE_RULE_ID;
      } else {
        seenRuleIDs.push(item.RuleID);
      }
    } else {
      error = hasGroup
        ? CORRELATION_RULE_EXCEPTIONS.NO_ID_GROUP
        : CORRELATION_RULE_EXCEPTIONS.NO_RULE_ID_SEQUENCE;
    }

    if (!hasGroup) {
      // item.ID is not required when Transitions are not specified, but RuleIDs still are
      const stepID = hasTransitions ? item.ID : item.RuleID || uuidv4();
      if (index !== sequence.length - 1) {
        const toNodeID = sequence[index + 1][hasTransitions ? 'ID' : 'RuleID'];
        if (hasTransitions) {
          transitionMap[stepID] = { [toNodeID]: true };
        } else {
          edges.push({
            id: `${stepID}-to-${toNodeID}`,
            source: stepID,
            target: toNodeID,
            type: FlowChartElementTypes.TransitionEdge,
            markerEnd,
            data: {
              transitionData: null,
              ...(error ? { error } : {}),
            } as TransitionEdgeData,
          });
        }
      }

      nodes.push({
        id: stepID,
        type: FlowChartElementTypes.StepNode,
        data: {
          stepData: item,
          hideHandles: hasGroup,
          isLastNode: index === sequence.length - 1 && !showAlertNode,
          ...(error ? { error } : {}),
        } as StepNodeData,
        position: { x: 0, y: index * NODE_SPACING },
      });
    }
    return { ...item, ...(error ? { error } : {}) };
  });

  if (hasGroup) {
    const minMatchCount = parsedRule?.MinMatchCount || 0;
    nodes = [
      {
        id: 'group',
        type: FlowChartElementTypes.GroupNode,
        data: {
          stepList,
          showHandles: showAlertNode,
          minMatchCount,
        } as GroupNodeData,
        position: { x: 0, y: 0 },
      },
    ];
  }

  (transitions || []).forEach(transition => {
    if (!transition) {
      return;
    }
    const { From, To } = transition;
    if (transitionMap[From]?.[To]) {
      transitionMap[From][To] = false;
      edges.push({
        id: transition.ID,
        source: From,
        target: To,
        type: FlowChartElementTypes.TransitionEdge,
        markerEnd,
        data: {
          transitionData: transition,
        } as TransitionEdgeData,
      });
    } else if (transitionMap[From]?.[To] === false) {
      edges.push({
        id: `${From}-to-${To}`,
        source: From,
        target: To,
        type: FlowChartElementTypes.TransitionEdge,
        markerEnd,
        data: {
          error: CORRELATION_RULE_EXCEPTIONS.TOO_MANY_TRANSITIONS,
        } as TransitionEdgeData,
      });
    } else {
      transitionMap.outOfOrderTransitions.push(transition);
    }
  });

  const { outOfOrderTransitions, ...missingTransitionMap } = transitionMap;
  Object.entries(missingTransitionMap).forEach(([from, toMap]) => {
    Object.entries(toMap).forEach(([to, needsEdge]) => {
      if (needsEdge) {
        edges.push({
          id: `${from}-to-${to}`,
          source: from,
          target: to,
          type: FlowChartElementTypes.TransitionEdge,
          markerEnd,
          data: {
            error: CORRELATION_RULE_EXCEPTIONS.MISSING_TRANSITION,
          } as TransitionEdgeData,
        });
      }
    });
  });

  if (showAlertNode) {
    const lastNodeId = hasGroup ? 'group' : nodes[nodes.length - 1]?.id;

    nodes.push({
      id: 'alert',
      type: FlowChartElementTypes.AlertNode,
      position: {
        x: ((hasGroup ? GROUP_NODE_WIDTH_PX : STEP_NODE_WIDTH_PX) - ALERT_NODE_WIDTH_PX) / 2,
        y: hasGroup
          ? group.length * (STEP_NODE_MAX_HEIGHT_PX + 4) + 20
          : nodes.length * NODE_SPACING,
      },
    });

    edges.push({
      id: `${lastNodeId}-to-alert`,
      source: lastNodeId,
      target: 'alert',
      type: FlowChartElementTypes.TransitionEdge,
      markerEnd,
      data: null,
    });
  }

  return {
    nodes,
    edges,
    ...(outOfOrderTransitions.length
      ? { warning: CORRELATION_RULE_EXCEPTIONS.INVALID_TRANSITIONS }
      : {}),
  };
};

export const parseYAMLIntoSections = (ruleYAML: string) => {
  let parsedYaml: CorrelationRuleData[];
  try {
    parsedYaml = yaml.load(ruleYAML) as CorrelationRuleData[];
  } catch (error) {
    return null;
  }
  if (!Array.isArray(parsedYaml) || parsedYaml.length < 1) {
    return null;
  }

  return parsedYaml[0];
};

export const checkIsValid = (parsedYaml: CorrelationRuleData) => {
  // if it contains the "Group" or "Sequence" sections
  return (
    parsedYaml &&
    Object.keys(parsedYaml).find(value =>
      [YAMLField.Sequence, YAMLField.Group].includes(value as YAMLField),
    ) &&
    (parsedYaml?.Sequence?.length || parsedYaml?.Group?.length) &&
    !(
      // if the rule combines sections that are not supposed to go together, show an error
      (
        Object.keys(parsedYaml).find(value => SequenceFields.includes(value as YAMLField)) &&
        Object.keys(parsedYaml).find(value => GroupFields.includes(value as YAMLField))
      )
    )
  );
};

// This checks if the section of the Correlation Rule YAML is logically the
// same as the template. Since comments are not preserved when the YAML is
// parsed, this ensures that if the section is the same as the template that
// the template comments will be put back

const templatesByField = {
  Sequence: DEFAULT_CORRELATION_RULE_YAML_SEQUENCE,
  Transitions: DEFAULT_CORRELATION_RULE_YAML_TRANSITIONS,
  Group: DEFAULT_CORRELATION_RULE_YAML_GROUP,
  MatchCriteria: DEFAULT_CORRELATION_RULE_YAML_MATCH_CRITERIA,
  Schedule: DEFAULT_CORRELATION_RULE_YAML_SCHEDULE,
  EventEvaluationOrder: DEFAULT_CORRELATION_RULE_YAML_EVALUATION_ORDER,
  LookbackWindowMinutes: DEFAULT_CORRELATION_RULE_YAML_LOOKBACK_MINUTES,
};

// Checks to see if a section, when parsed, is the same as the template.
// If it is, then it returns the template instead.
// This preserves comments until they've made a change to the section
export const getYAMLSectionByField = (
  fieldData: CorrelationRuleData,
  field: YAMLField,
  templateOverride?: YAMLField, // for saving the section as something different (for "Group" and "Sequence" sections)
) => {
  const getDumpedData = () => {
    // For objects, yaml.dump doesn't space out the keys properly, so this adjusts the spacing to prevent
    // parsing errors. The only field this applies to is MatchCriteria
    if (field === YAMLField.MatchCriteria || field === YAMLField.Schedule) {
      const dumpedFieldData = yaml.dump(fieldData, { noArrayIndent: true });
      return `${field}:\n    ${dumpedFieldData.replace(/\n/g, `\n    `)}`;
    }
    return yaml.dump({ [templateOverride ?? field]: fieldData });
  };
  return isEqual(fieldData, (yaml.load(templatesByField[field]) as CorrelationRuleData)[field])
    ? templatesByField[templateOverride ?? field]
    : getDumpedData();
};
